import TruncatedTypography from '@/src/components/common/ui/truncatedTypography'
import { Airline } from '@/src/hooks/srp/airlineFilterManager'
import { Box, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import React from 'react'

interface AirlineCardProps extends Airline {
    onSelect?: (id: string) => void
    hoverBorderColor?: string
    isMobile?: boolean
}

const AirlineCard: React.FC<AirlineCardProps> = ({
    id,
    name,
    logoUrl,
    price,
    isSelected,
    onSelect,
    hoverBorderColor,
    isMobile
}) => {
    if (isMobile) {
        return (
            <Box
                onClick={() => onSelect?.(id)}
                sx={{
                    width: 124,
                    height: 41,
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? theme => theme.palette.action.selected : 'background.paper',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '8px',
                    p: '4px',
                    mr: 1.5,
                    flexShrink: 0,
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: isSelected ? 'primary.main' : 'grey.400'
                    }
                }}
            >
                <Box
                    component="img"
                    src={logoUrl}
                    alt={name}
                    sx={{
                        width: 16,
                        height: 16,
                        objectFit: 'contain',
                        flexShrink: 0
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flex: 1
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '100%',
                            letterSpacing: 0,
                            color: '#333333',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            maxWidth: '90px'
                        }}
                    >
                        {name}
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '100%',
                            letterSpacing: 0,
                            color: isSelected ? 'primary.main' : 'text.primary',
                            mt: '2px'
                        }}
                    >
                        {price}
                    </Typography>
                </Box>
            </Box>
        )
    }

    // Desktop version remains the same
    return (
        <Box
            onClick={() => onSelect?.(id)}
            sx={theme => ({
                minWidth: 140,
                maxWidth: 150,
                height: 52,
                borderRadius: '10px',
                border: '1.5px solid',
                borderColor: isSelected ? alpha(theme.palette.link.main, 0.7) : 'borders.default',
                bgcolor: isSelected ? alpha(theme.palette.primary.light, 0.2) : 'white',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                px: 1.5,
                py: 2,
                mr: 1.5,
                flexShrink: 0,
                transition: 'border-color 0.2s',
                '&:hover': {
                    borderColor: isSelected ? 'link.main' : hoverBorderColor || 'grey.300'
                }
            })}
        >
            <Box
                component="img"
                src={logoUrl}
                alt={name}
                sx={{
                    width: 20,
                    height: 20,
                    objectFit: 'contain',
                    mr: 1.2,
                    borderRadius: '4px'
                }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <TruncatedTypography
                    fontWeight={400}
                    fontSize={12}
                    color="#333333"
                    lineHeight={1.2}
                    maxWidth={70}
                    maxLines={1}
                >
                    {name}
                </TruncatedTypography>
                <Typography fontWeight={600} fontSize={12} color="#333333" lineHeight={1.2}>
                    {price}
                </Typography>
            </Box>
        </Box>
    )
}

export default AirlineCard
